import { Injectable } from '@angular/core';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { CollectionService } from 'src/app/collections/collection.service';
import { Asset } from 'src/app/platform/busines-data/Framework/Asset';
import { AssetState } from 'src/app/platform/busines-data/Framework/asset-state';
import { Modes } from 'src/app/platform/busines-data/Framework/mode';
import { Region } from 'src/app/platform/busines-data/Framework/Region';
import { Section } from 'src/app/platform/busines-data/Framework/Section';
import { AssetInfoViewService } from 'src/app/template/asset-info-view/asset-info-view/asset-info-view.service';
import { VisualizationMode } from 'src/app/template/menu/menu.service';
import { TemplateService } from 'src/app/template/template.service';
import { CameraControl } from './graphics/graphics-camera-controler.service';
import { GraphicsMaterials } from './graphics/graphics-materials';
import { DTSelectionService } from './selection/selection.service';

/**
 * View modes that colour only the regions visible at paint time. After a hide
 * undo/redo they must be reapplied so the restored region is repainted. Every
 * other mode either paints all regions or, via the DEFAULT branch, resets every
 * element's visibility, which would cancel the toggle (see renderHideState).
 */
const VISIBLE_SCOPED_VIEW_MODES: ReadonlySet<VisualizationMode> = new Set<VisualizationMode>([
    'Degradation', 'RemainingLife', 'CorrosionRate', 'CorrosionRiskIndex', 'Risk',
]);

/**
 * SceneGraphRenderer is the format-aware rendering engine that correctly
 * restores visual state across all 4 format/LightModel combinations
 * (new/old x LightModel/no-LightModel) for all 3 modes (Model, Regions, Sections).
 *
 * This service replaces the broken processAssetState() in AssetStateManager
 * with a unified renderer that handles both new and old scene graph formats.
 * It is the core of the undo/redo visual restoration pipeline.
 *
 * ALL format branching (isNewModelMode, isLightModel) lives EXCLUSIVELY
 * inside this service. It contains ZERO THREE.js rendering logic of its own
 * -- pure orchestration via delegation to Region, Section, and Asset methods.
 *
 * Phase 3 -- Plans 01 (format matrix) + 02 (hide + root)
 */
@Injectable({ providedIn: 'root' })
export class SceneGraphRenderer {

    // -----------------------------------------------------------------------
    // Constructor-injected dependencies
    // -----------------------------------------------------------------------

    constructor(
        private assetManager: AssetManager,
        private assetInfoService: AssetInfoViewService,
        private templateService: TemplateService,
        private dtSelectionService: DTSelectionService,
        private graphicsMaterials: GraphicsMaterials,
        private collectionService: CollectionService,
        private cameraControl: CameraControl,
    ) {}

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    /**
     * Full state restoration for non-hide undo/redo.
     *
     * Steps IN ORDER:
     * 1. If root state -- delegate to renderRootState()
     * 2. Restore region/section visibility
     * 3. Render mode visuals (the 12-cell format/mode matrix), then re-apply the visibility map
     * 3b. New format: re-apply the mode's section sub-group visuals over the map
     * 3c. Light model: the map must not re-show what the mode hides (light regions outside
     *     MODEL, loaded structure regions in MODEL)
     * 4. Set mode on asset AFTER visuals (mode setter has side effects)
     * 5. Synchronize all UI components
     * 6. Restore camera smoothly (non-hide only)
     */
    public renderState(asset: Asset, state: AssetState): void {
        // Root state has its own restoration path
        if (state.isRoot) {
            this.renderRootState(asset);
            return;
        }

        // 1. Restore region/section visibility first (needed by GetVisibleRegions)
        asset.setRegionsVisibility(state.regions);

        // 2. Render mode visuals via the 12-cell matrix
        this.renderModeVisuals(asset, state.mode);

        // 3. Re-apply visibility map: renderModeVisuals may override section
        //    visibility (e.g. applyRegionModeVisuals sets visibleSectionLayer=true).
        //    The snapshot visibility must have the final say.
        asset.setRegionsVisibility(state.regions);

        // 3b. New format: setRegionsVisibility also wrote section.surfaceGroup.visible
        //     (per-element meshes), which the view mode owns. Re-apply the mode's
        //     sub-group visuals so MODEL/REGIONS draw the lightSection, not every element mesh.
        this.reapplyNewFormatSectionVisuals(asset, state.mode);

        // 3c. Light model outside MODEL: steps 1 and 3 ran setRegionsVisibility while asset.mode
        //     was still the OLD mode (set in step 4). From MODEL, Asset.setRegionsVisibility applies
        //     the map to lightCampaign too, and light regions share their Guid with the structure
        //     region, so the map re-showed the light-model copy of the entered region that
        //     renderModeVisuals had hidden (syncLightModelAttachment then re-attached the light model).
        if (asset.isLightModel && state.mode !== 'MODEL') {
            asset.GetLightModelRegions()?.forEach(region => {
                region.visible = false;
            });
        } else if (asset.isLightModel && state.mode === 'MODEL') {
            // MODEL mirror: AssetStateCapture.captureRegionVisibility records MODEL states with the
            // light-region visibility under the shared Guid, so step 3 re-showed the loaded structure
            // region that renderNewFormat / renderOldFormat MODEL had hidden (drawn inside the light
            // model on redo). Same rule as those MODEL branches and Asset.setVisibilityForAll.
            asset.GetAllStructureRegions()?.forEach(region => {
                region.visible = false;
            });
        }

        // 4. CRITICAL: Set mode on asset AFTER visual rendering.
        //    The mode setter has side effects (clears borders/linears in MODEL,
        //    calls clearModelTemporalElement in REGIONS/SECTIONS). Setting it
        //    after rendering ensures the cleanup is a no-op because visuals
        //    already match the target mode.
        asset.mode = state.mode;

        // 5. Synchronize all UI components
        this.syncUI(asset);

        // 6. Restore camera smoothly (per locked decision #4)
        if (this.cameraControl && state.position && state.target) {
            this.cameraControl.restoreCameraSmooth(state.position, state.target);
        }
    }

    /**
     * Hide-specific undo/redo rendering -- direction-aware visibility toggle.
     *
     * Per locked decisions:
     * - NO camera restoration -- camera stays at user's current position
     * - Direction-aware visibility:
     *   - undo: Element reappears (visible = !objectVisibility). No selection highlight.
     *   - redo: Element disappears again (visible = objectVisibility). Instant.
     * - Cross-mode: If state.mode differs from asset.mode, triggers mode transition
     * - Region visibility map is intentionally NOT applied for hide states
     * - CRITICAL: Never modifies state.objectVisibility (immutable snapshot field)
     *
     * @param asset The asset being manipulated
     * @param state The hide state to render
     * @param direction 'undo' to reappear, 'redo' to disappear again
     */
    public renderHideState(asset: Asset, state: AssetState, direction: 'undo' | 'redo'): void {
        const modeChanged = state.mode !== asset.mode;

        // 1. Direction-aware visibility toggle on the actual scene object.
        //    state.objectVisibility captures the visibility at hide time.
        //    - undo: flip it (hidden element reappears)
        //    - redo: restore it (element disappears again)
        if (state.object) {
            if (direction === 'undo') {
                state.object.visible = !state.objectVisibility;
            } else {
                state.object.visible = state.objectVisibility;
            }
        }

        // Ensure resolved Region/Section wrappers are visible on undo.
        // Some hide flows toggle wrapper groups directly, others rely on IDs.
        if (direction === 'undo') {
            this.restoreHideTargetBaseVisibility(asset, state);
        }

        // 2. Cross-mode restoration: switch back to the original mode if needed.
        //    Example: Hide in REGIONS, user now in MODEL -> undo switches to REGIONS.
        if (modeChanged) {
            this.renderModeTransition(asset, state.mode);
        }

        // NOTE: Region visibility map is NOT applied here. The hide state's
        // regions map captures post-hide visibility (the hidden object is
        // already invisible in the map). Applying it on undo would re-hide
        // the object we just made visible. The object toggle above is the
        // only visibility change needed for hide undo/redo.

        // 3. New-format hide undo needs subgroup restoration when mode did not
        //    change. Group visibility is captured, but mode-specific subgroup
        //    visibility (lightSection/surface/linear/border) is not.
        if (!modeChanged && direction === 'undo') {
            this.restoreNewFormatHideUndoVisuals(asset, state);
        }

        // Narrow fallback for new-format/no-LightModel region-hide undo:
        // if undo still leaves no visible structure regions, force-restore
        // the target structure region in MODEL visuals.
        if (direction === 'undo') {
            this.enforceModelRegionUndoVisibility(asset, state);
        }

        // 4. UI sync. Cross-mode already synced inside renderModeTransition().
        //    Reapply the view mode only for modes that colour visible regions
        //    (see VISIBLE_SCOPED_VIEW_MODES); every other mode either paints all
        //    regions or, via DEFAULT, resets element visibility.
        if (!modeChanged) {
            this.syncUI(asset, false, VISIBLE_SCOPED_VIEW_MODES.has(asset.viewMode));
        }

        // 5. Re-assert the single-object toggle last. Both the cross-mode
        //    transition and the visible-scoped reapply can write element
        //    visibility (DEFAULT resets every structure element; Risk re-shows
        //    every critical area), so the hide/unhide must be the final write.
        if (state.object) {
            state.object.visible = direction === 'undo'
                ? !state.objectVisibility
                : state.objectVisibility;
        }
    }

    /**
     * Root state restoration -- fresh load default view.
     *
     * Delegates to Asset.resetToDefault() which already handles isNewModelMode
     * branching, sets all campaigns visible, calls view-mode methods.
     * Camera positioning is handled by the caller (not the renderer)
     * because root camera position depends on the asset's bounding box.
     */
    public renderRootState(asset: Asset): void {
        asset.resetToDefault();
        this.syncUI(asset);
        this.dtSelectionService.regenerateLegendIfNeeded(asset);
    }

    /**
     * Mode-only restoration. Used when undoing a hide that crosses modes
     * (e.g. hide in REGIONS, user is now in MODEL, undo switches back to REGIONS).
     *
     * Steps:
     * 1. Render mode visuals
     * 2. Set mode on asset
     * 3. Synchronize all UI components
     */
    public renderModeTransition(asset: Asset, targetMode: Modes): void {
        this.renderModeVisuals(asset, targetMode);
        asset.mode = targetMode;
        this.syncUI(asset);
    }

    /**
     * New format only: re-applies the mode's section sub-group visuals after a visibility map
     * was applied. The rule lives in Asset.reapplyNewFormatSectionVisuals (see its doc comment)
     * so DTSelectionService, which SceneGraphRenderer injects, can apply it too after explorer
     * region/section (un)checks without a circular injection.
     */
    public reapplyNewFormatSectionVisuals(asset: Asset, mode: Modes): void {
        asset?.reapplyNewFormatSectionVisuals(mode);
        // was (moved verbatim to Asset.reapplyNewFormatSectionVisuals, asset → this):
        // if (!asset?.isNewModelMode) return;
        //
        // const regions = asset.GetStructureVisibleRegions() || [];
        // switch (mode) {
        //     case 'MODEL':
        //         if (asset.isLightModel) return;
        //         regions.forEach(region => region.setViewOnModelMode());
        //         break;
        //     case 'REGIONS':
        //         regions.forEach(region => {
        //             if (region.isStructural) region.setViewOnRegionMode();
        //         });
        //         break;
        //     case 'SECTIONS':
        //         regions.forEach(region => {
        //             region.sections?.forEach(section => {
        //                 if (section.visible) section.setViewOnSectionMode();
        //             });
        //         });
        //         break;
        // }
    }

    /**
     * Calls UI synchronization points after rendering.
     *
     * Items 1-5 always run (sidebar, documents, subscribers, legends, view mode).
     * Items 6-8 (navigation close, panel close, functionality reset) only run
     * when modeChanged is true -- hide undo/redo within the same mode should
     * not close panels or reset functionality.
     *
     * Additionally, items 7-8 (panel close + functionality reset) are skipped
     * during INSPECTIONINSERTION even when modeChanged is true: inspection
     * insertion survives undo/redo (parity with prod's undo guard).
     *
     * @param asset The asset to synchronize UI for
     * @param modeChanged Whether a mode transition occurred (default: true).
     *   When false, skips navigation close / panel close / functionality reset.
     *   When true, panel close + functionality reset are still skipped while
     *   functionalityMode is 'INSPECTIONINSERTION'.
     * @param reapplyViewMode Whether to reapply the current view mode (default: true).
     */
    public syncUI(asset: Asset, modeChanged: boolean = true, reapplyViewMode: boolean = true): void {
        // 1. Sidebar: update region/section list.
        // Mode-aware selection (ported from old AssetStateManager.getAssetInfoRegions):
        // include light-model regions only when viewing the full MODEL of a light-model
        // asset; in REGIONS/SECTIONS we want just the structure-visible regions so the
        // sidebar doesn't surface hidden light-model entries.
        const sidebarRegions = asset.isLightModel && asset.mode === 'MODEL'
            ? asset.GetStructureVisibleRegionsAndLightModel()
            : asset.GetStructureVisibleRegions();
        this.assetInfoService.setRegionAndSections(sidebarRegions);

        // 2. Documents: update document overlay visibility
        asset.changeDocumentsVisibilityMode();

        // 3. Subscribers: notify all state-change listeners
        asset.assetStateList.stateChanged.next(true);

        // 4. Legends: regenerate color legends for current view mode
        this.dtSelectionService.regenerateLegendIfNeeded(asset);

        // 5. View mode: reapply view mode materials/colors. Skipped by
        //    renderHideState for view modes that paint all regions, and for
        //    DEFAULT, whose changeToDefaultViewMode() sets every structure
        //    element visible and would cancel the toggle.
        if (reapplyViewMode) {
            this.assetManager.reapplyCurrentViewMode();
        }

        // Items 6-8 only execute when mode actually changed.
        // Hide undo/redo within the same mode should not disrupt open panels.
        if (modeChanged) {
            // 6. Navigation: close section navigation panel
            this.assetManager.isNavigationSectionAvailable = false;

            // Inspection insertion survives undo/redo: prod's undo() guarded the
            // panel-close + functionality reset behind
            // functionalityMode !== 'INSPECTIONINSERTION'.
            if (this.assetManager.functionalityMode !== 'INSPECTIONINSERTION') {
                // 7. Panels: close element type / critical-area / element-insertion selector panels
                this.templateService.showElementTypeSelector = false;
                this.templateService.showCriticalAreaSelectorComponent = false;
                this.templateService.showElementInsertionTypeSelector = false;

                // 8. Functionality mode: reset to NONE
                this.assetManager.setFunctionalityModeNone();
            }
        }
    }

    // -----------------------------------------------------------------------
    // Private: Format branching (the core of the rendering matrix)
    // -----------------------------------------------------------------------

    /**
     * Routes to new-format or old-format rendering based on asset.isNewModelMode.
     * ALL format branching is encapsulated here.
     * Also clears assetManager.currentSection when rendering REGIONS visuals
     * (prod parity with processAssetState's REGIONS branch).
     */
    private renderModeVisuals(asset: Asset, mode: Modes): void {
        // Prod parity: restoring a REGIONS-mode state clears the current section
        // (prod's processAssetState REGIONS branch set currentSection = null).
        // A stale currentSection makes FinishInsertAreaInspection resolve area
        // targets against the wrong section after undo (empty save -> HTTP 424).
        if (mode === 'REGIONS') {
            this.assetManager.currentSection = null;
        }
        if (asset.isNewModelMode) {
            this.renderNewFormat(asset, mode);
        } else {
            this.renderOldFormat(asset, mode);
        }
    }

    /**
     * New scene graph format rendering (4 cells: 2 LightModel x 2 modes relevant).
     *
     * New format uses Section view-mode methods (setViewOnModelMode,
     * setViewOnRegionMode, setViewOnSectionMode) which control visibility
     * of lightSectionGroup, surfaceGroup, linearGroup, and borderGroup.
     *
     * Matrix:
     * | LightModel | MODEL | REGIONS | SECTIONS |
     * |------------|-------|---------|----------|
     * | Yes        | Hide structure, show LightModel | Hide LightModel, show structure via view-mode methods | Hide LightModel |
     * | No         | setViewOnModelMode per region | setViewOnRegionMode per region | (handled by visibility map) |
     */
    private renderNewFormat(asset: Asset, mode: Modes): void {
        switch (mode) {
            case 'MODEL':
                if (asset.isLightModel) {
                    // LightModel campaign replaces everything in MODEL mode.
                    // Hide ALL structure regions, show ALL LightModel regions.
                    // Do NOT call setViewOnModelMode() -- that would show
                    // lightSectionGroup alongside the LightModel campaign.
                    const structureRegions = asset.GetAllStructureRegions();
                    structureRegions?.forEach(region => {
                        region.visible = false;
                    });
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = true;
                    });
                } else {
                    // No LightModel: call setViewOnModelMode() on each visible
                    // structure region (shows lightSectionGroup per section).
                    const visibleRegions = asset.GetVisibleRegions();
                    visibleRegions?.forEach(region => {
                        region.setViewOnModelMode();
                    });
                }
                break;

            case 'REGIONS':
                // Hide LightModel if present
                if (asset.isLightModel) {
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = false;
                    });
                }
                // For each visible structure region, apply view-mode methods.
                // Structural regions use setViewOnRegionMode(), non-structural
                // fall back to border/linear rendering (replicating selectRegion pattern).
                this.applyRegionModeVisuals(asset, asset.GetVisibleRegions());
                break;

            case 'SECTIONS': {
                // Hide LightModel if present
                if (asset.isLightModel) {
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = false;
                    });
                }
                // setRegionsVisibility restores which sections are visible (root group),
                // but sub-group state (surfaceGroup, linearGroup, lightSectionGroup)
                // is NOT captured in the visibility map. Apply setViewOnSectionMode()
                // to visible sections so surface/linear groups show instead of lightSection.
                const sectionsRegions = asset.GetVisibleRegions();
                sectionsRegions?.forEach(region => {
                    region.sections?.forEach(section => {
                        if (section.visible) {
                            section.setViewOnSectionMode();
                        }
                    });
                });
                break;
            }
        }
    }

    /**
     * Old scene graph format rendering (4 cells: 2 LightModel x 2 modes relevant).
     *
     * Old format uses border/linear rendering operations (renderBorderofElements,
     * renderLinearElements, clearBorderofElements) rather than Section view-mode methods.
     *
     * Matrix:
     * | LightModel | MODEL | REGIONS | SECTIONS |
     * |------------|-------|---------|----------|
     * | Yes        | Hide structure, show LightModel | Hide LightModel, show structure + borders/linears | Hide LightModel + borders/linears |
     * | No         | Clear borders/linears on visible regions | Render borders/linears on visible regions | Render borders/linears on visible regions |
     */
    private renderOldFormat(asset: Asset, mode: Modes): void {
        switch (mode) {
            case 'MODEL':
                if (asset.isLightModel) {
                    // Hide structure regions, show LightModel regions
                    const structureRegions = asset.GetVisibleRegions();
                    structureRegions?.forEach(region => {
                        region.clearBorderofElements();
                        region.renderLinearElements(false);
                        region.visible = false;
                    });
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = true;
                    });
                } else {
                    // Clear borders and linears on all visible regions
                    const visibleRegions = asset.GetVisibleRegions();
                    visibleRegions?.forEach(region => {
                        region.clearBorderofElements();
                        region.renderLinearElements(false);
                    });
                }
                break;

            case 'REGIONS':
                // Hide LightModel if present
                if (asset.isLightModel) {
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = false;
                    });
                }
                // Render borders/linears on visible regions
                this.applyBorderAndLinearVisuals(asset, asset.GetVisibleRegions());
                break;

            case 'SECTIONS':
                // Hide LightModel if present
                if (asset.isLightModel) {
                    const lightModelRegions = asset.GetLightModelRegions();
                    lightModelRegions?.forEach(region => {
                        region.visible = false;
                    });
                }
                // Render borders/linears on visible regions
                this.applyBorderAndLinearVisuals(asset, asset.GetVisibleRegions());
                break;
        }
    }

    // -----------------------------------------------------------------------
    // Private: Shared rendering helpers
    // -----------------------------------------------------------------------

    /**
     * Applies REGIONS-mode visuals for new-format assets.
     * Structural regions use setViewOnRegionMode(), non-structural regions
     * fall back to border/linear rendering (replicating the selectRegion pattern
     * from DTSelectionService).
     */
    private applyRegionModeVisuals(asset: Asset, regions: Region[]): void {
        if (!regions) return;

        for (const region of regions) {
            if (region.isStructural) {
                region.visible = true;
                region.visibleSectionLayer = true;
                region.setViewOnRegionMode();
            } else {
                // Non-structural regions in new format: fall back to
                // border/linear rendering (same as old format).
                if (!asset.assetInfo.HasOverlappedGeometry) {
                    region.renderBorderofElements(GraphicsMaterials.borderMaterialLineBasic);
                }
                region.visible = true;
                region.visibleAllChild = true;
                region.visibleSectionLayer = true;
                region.renderLinearElements(true);
            }
        }
    }

    /**
     * Applies border and linear rendering for old-format assets.
     * Replicates the HasOverlappedGeometry guard from the existing codebase.
     */
    private applyBorderAndLinearVisuals(asset: Asset, regions: Region[]): void {
        if (!regions) return;

        for (const region of regions) {
            if (!asset.assetInfo.HasOverlappedGeometry) {
                region.renderBorderofElements(GraphicsMaterials.borderMaterialLineBasic);
            }
            region.renderLinearElements(true);
        }
    }

    /**
     * Reapplies mode-specific section subgroup visibility after hide undo for
     * new-format assets in the same mode (no mode transition).
     */
    private restoreNewFormatHideUndoVisuals(asset: Asset, state: AssetState): void {
        if (!asset.isNewModelMode) return;

        // MODEL + LightModel intentionally shows only light model campaign.
        if (state.mode === 'MODEL' && asset.isLightModel) return;

        const region = this.resolveHideTargetRegion(asset, state);
        if (!region) return;

        const section = this.resolveHideTargetSection(region, state);

        switch (state.mode) {
            case 'MODEL':
                this.restoreModelModeSectionVisuals(region, section);
                break;
            case 'REGIONS':
                this.restoreRegionsModeSectionVisuals(region, section);
                break;
            case 'SECTIONS':
                this.restoreSectionsModeSectionVisuals(region, section);
                break;
        }
    }

    private restoreModelModeSectionVisuals(region: Region, section: Section | null): void {
        region.visible = true;

        if (section) {
            section.visible = true;
            section.setViewOnModelMode();
            return;
        }

        region.sections?.forEach(section => {
            if (section.visible) {
                section.setViewOnModelMode();
            }
        });
    }

    private restoreRegionsModeSectionVisuals(region: Region, section: Section | null): void {
        region.visible = true;

        if (section) {
            section.visible = true;
            section.setViewOnRegionMode();
            return;
        }

        region.setViewOnRegionMode();
    }

    private restoreSectionsModeSectionVisuals(region: Region, section: Section | null): void {
        region.visible = true;

        if (section) {
            section.visible = true;
            section.setViewOnSectionMode();
            return;
        }

        region.sections?.forEach(section => {
            if (section.visible) {
                section.setViewOnSectionMode();
            }
        });
    }

    private restoreHideTargetBaseVisibility(asset: Asset, state: AssetState): void {
        const targetRegions = this.resolveAllHideTargetRegions(asset, state);
        if (!targetRegions.length) return;

        for (const region of targetRegions) {
            region.visible = true;

            const section = this.resolveHideTargetSection(region, state);
            if (section) {
                section.visible = true;
            } else {
                this.ensureRegionHasVisibleSections(region);
            }
        }
    }

    private ensureRegionHasVisibleSections(region: Region): void {
        const sections = region.sections || [];
        if (!sections.length) return;

        const hasVisibleSection = sections.some(section => section.visible);
        if (hasVisibleSection) return;

        sections.forEach(section => {
            section.visible = true;
        });
    }

    private resolveAllHideTargetRegions(asset: Asset, state: AssetState): Region[] {
        const regions = new Array<Region>();

        if (state.regionId) {
            // Light-model regions share Guids with the structure regions but are
            // only ever visible in MODEL; outside MODEL the typed regions are the
            // visible ones. Restoring both copies of a Guid would show the light
            // model in REGIONS/SECTIONS (or the structure region in MODEL).
            const useLightModel = asset.isLightModel && state.mode === 'MODEL';
            const candidates: Region[] = useLightModel
                ? (asset.GetLightModelRegions() || [])
                : (asset.GetRegionsAllTypes?.() || []);
            for (const region of candidates) {
                if (region?.Guid === state.regionId && !regions.includes(region)) {
                    regions.push(region);
                }
            }
        }

        if (!regions.length) {
            const resolved = this.resolveHideTargetRegion(asset, state);
            if (resolved) {
                regions.push(resolved);
            }
        }

        return regions;
    }

    private enforceModelRegionUndoVisibility(asset: Asset, state: AssetState): void {
        if (state.sectionId) return;
        if (!asset.isNewModelMode || asset.isLightModel) return;
        if (state.mode !== 'MODEL') return;

        const visibleStructureRegions = asset.GetStructureVisibleRegions() || [];
        if (visibleStructureRegions.length > 0) return;

        const allStructureRegions = asset.GetAllStructureRegions() || [];
        if (!allStructureRegions.length) return;

        let targetRegions = state.regionId
            ? allStructureRegions.filter(region => region.Guid === state.regionId)
            : [];

        if (!targetRegions.length) {
            const byObject = this.resolveHideTargetRegion(asset, state);
            if (byObject && allStructureRegions.includes(byObject)) {
                targetRegions = [byObject];
            }
        }

        for (const region of targetRegions) {
            region.visible = true;
            for (const section of (region.sections || [])) {
                section.visible = true;
                section.setViewOnModelMode();
            }
        }
    }

    private resolveHideTargetRegion(asset: Asset, state: AssetState): Region | null {
        const structureRegions = asset.GetAllStructureRegions() || [];
        const lightModelRegions = asset.isLightModel ? (asset.GetLightModelRegions() || []) : [];
        const candidateRegions = [...structureRegions, ...lightModelRegions];
        if (!candidateRegions.length) return null;

        if (state.regionId) {
            const byId = candidateRegions.find(x => x.Guid === state.regionId);
            if (byId) return byId;
        }

        if (!state.object) return null;

        return candidateRegions.find(region =>
            region.group === state.object || this.isDescendantOf(state.object, region.group)
        ) || null;
    }

    private resolveHideTargetSection(region: Region, state: AssetState): Section | null {
        if (!region.sections?.length) return null;

        if (state.sectionId) {
            const byId = region.sections.find(x => x.Guid === state.sectionId);
            if (byId) return byId;
        }

        if (!state.object) return null;

        return region.sections.find(section =>
            section.group === state.object || this.isDescendantOf(state.object, section.group)
        ) || null;
    }

    private isDescendantOf(node: any, ancestor: any): boolean {
        if (!node || !ancestor) return false;

        let current = node;
        while (current) {
            if (current === ancestor) {
                return true;
            }
            current = current.parent;
        }
        return false;
    }

}
